const BASE_COLORS = {
  black: '#000000',
  blue: '#091AE3',
  blueDarker: '#0817cc',
  blueLighter: '#2231e6',
  gray10: '#1A1A1A',
  gray20: '#333333',
  gray30: '#4D4D4D',
  gray40: '#666666',
  gray50: '#7F7F7F',
  gray55: '#949494',
  gray60: '#999999',
  gray70: '#B3B3B3',
  gray75: '#D6D6D6',
  gray80: '#D8D8D8',
  gray85: '#EAEAEA',
  gray90: '#FDFDFD',
  gray95: '#F2F2F2',
  gray98: '#FAFAFA',
  green: '#24B109',
  greenDarker: '#209f08',
  greenLighter: '#3ab922',
  orange: '#F49D37',
  orangeDarker: '#DC8D32',
  orangeLighter: '#F5A74B',
  red: '#E30936',
  redDarker: '#CC0831',
  redLighter: '#E6224A',
  white: '#FFFFFF',
  yellow: '#ffbb00',
  yellowDarker: '#e6a800',
  yellowLighter: '#ffc21a',
} as const;

const hexColorTester = /#([0-9A-F]{3}|[0-9A-F]{6})/i;

/**
 * Converts Hex and Opacity into Hex-Alpha. Accepts both 3 or 6 digit hex colors.
 *
 * opacity('#000000', 0.2) => '#00000033'
 * opacity('#000', 0.2) => '#0003'
 */
export const opacity = (hexColor: string, colorOpacity: number) => {
  if (colorOpacity < 0 || colorOpacity > 1) {
    throw new Error('colorOpacity argument out of range (0-1)');
  }

  if (hexColorTester.test(hexColor)) {
    if (hexColor.length === 7) {
      // #XXXXXX
      return (
        hexColor +
        Math.round(colorOpacity * 255)
          .toString(16)
          .padStart(2, '0')
      );
    }
    return hexColor + Math.round(colorOpacity * 15).toString(16);
  } else {
    throw new Error('Invalid hexColor');
  }
};

/**
 * Blends two colors together based on a ratio.
 * Replicates the behaviour of the `hexColor` using an opacity over the `blendingColor`.
 * `blendingColor` is #FFFFFF by default.
 */
export function blend(hexColor: string, blendRatio: number, blendingColor: string | null = null) {
  if (blendRatio < 0 || blendRatio > 1) {
    throw new Error('blendRatio argument out of range (0-1)');
  }

  if (!hexColorTester.test(hexColor)) {
    throw new Error('Invalid hexColor');
  }

  if (blendingColor === null && hexColor.length === 4) {
    blendingColor = BASE_COLORS.white.slice(0, 4);
  } else if (blendingColor === null) {
    blendingColor = BASE_COLORS.white;
  }

  if (!hexColorTester.test(blendingColor)) {
    throw new Error('Invalid blendingColor');
  }

  if (blendingColor.length !== hexColor.length) {
    throw new Error('hexColor and blendingColor must have the same length');
  }

  const colorBitDepth = hexColor.length === 7 ? 8 : 4;
  const colorMask = colorBitDepth === 8 ? 0xff : 0xf;

  const hexColorValue = parseInt(hexColor.slice(1), 16);
  const blendingColorValue = parseInt(blendingColor.slice(1), 16);

  const r = Math.round(
    // eslint-disable-next-line no-bitwise
    blendRatio * (hexColorValue >> (2 * colorBitDepth)) +
      // eslint-disable-next-line no-bitwise
      (1 - blendRatio) * (blendingColorValue >> (2 * colorBitDepth))
  );
  const g = Math.round(
    // eslint-disable-next-line no-bitwise
    blendRatio * ((hexColorValue >> colorBitDepth) & colorMask) +
      // eslint-disable-next-line no-bitwise
      (1 - blendRatio) * ((blendingColorValue >> colorBitDepth) & colorMask)
  );
  const b = Math.round(
    // eslint-disable-next-line no-bitwise
    blendRatio * (hexColorValue & colorMask) + (1 - blendRatio) * (blendingColorValue & colorMask)
  );

  return (
    // eslint-disable-next-line no-bitwise
    '#' + ((r << (2 * colorBitDepth)) + (g << colorBitDepth) + b).toString(16).padStart(hexColor.length - 1, '0')
  );
}

/**
 * NOTE: Avoid using the base colors directly when possible – it makes
 * restyling harder. Instead, define a semantic label (e.g 'disabledText')
 * or use an existing one if it makes sense and doesn't overload the meaning.
 */
const colors = {
  ...BASE_COLORS,

  // Project specific
  primary: '#0A0B09',
  secondary: '#FDE71E',

  highlight: 'rgba(9,26,227,1)',
  highlightShadow: 'rgba(9,26,227,0.2)',
  highlightActive: '#3A47E8',
  highlightInactive: 'rgba(9,26,227,0.5)',
  highlightDisabledBackground: '#CFE0FC',

  primaryButton: BASE_COLORS.blue,
  primaryButtonActive: BASE_COLORS.blueDarker,

  activeLight: '#F3F7FE',

  background: '#FBF9FB',
  filterBackground: '#D2E2FC',
  modalBackground: opacity(BASE_COLORS.black, 0.5),

  shadowBorder: opacity(BASE_COLORS.black, 0.1),
  inactive: '#B6B6B6',

  disabledButtonText: '#CBCFD6',
  disabledButtonBackground: '#F1F2F4',

  disabledIcon: BASE_COLORS.gray70,
  disabledText: BASE_COLORS.gray80,
  disabledBorder: BASE_COLORS.gray95,
  disabledBackground: BASE_COLORS.gray90,

  danger: BASE_COLORS.red,
  dangerPlaceholder: '#E77E7F',
  dangerLight: '#F1A4A7',
  dangerBackground: '#FADADB',
  dangerActive: '#BD2B30',
  dangerFocus: 'rgba(230,52,58,0.5)',

  interrupt: BASE_COLORS.orange,

  error: BASE_COLORS.red,

  warning: '#FFEB33',
  newWarning: '#F5920A',
  warningLight: '#FFF6A2',
  warningActive: '#FDE71E',

  success: BASE_COLORS.green,
  ok: BASE_COLORS.green,
  successLight: '#C8F3D9',
  successActive: '#1C7D3A',
  successFocus: 'rgba(43,196,91,0.5)',

  info: '#6890BF',

  tableHeaderBackground: BASE_COLORS.gray98,
  tableHeaderBorderBottom: '#B2B2B2',
  tableText: '#646464',
  tableBorder: '#D3D3D3',

  geofencePrimary: '#FDE71E',
  geofenceSecondary: '#BFAF18',

  formButton: '#F3F5F7',

  fieldLabel: BASE_COLORS.black,
  tooltipBackground: '#1B2C40',

  darkBackground: '#353535',
  lightBackground: '#f0f2f5',
  activeLightBackground: '#e4e8ed',
  canvasBorder: '#dce0e8',
  selectorBackground: '#f5f9ff',
  selectorBorder: '#dadee6',

  fleetDarkGray: '#9DA1AA',
  fleetOffGray: '#DADEE6',
  fleetBlack: '#0a132b',
  fleetAlmostBlack: '#050916',
  fleetBlue: BASE_COLORS.blue,
  fleetDarkGreen: '#146604',
  fleetDarkBlue: '#050916',
  fleetElectricYellow: '#EBF60A',
  fleetSlateGray: '#3D4253',

  arrowButtonBackgroundHover: '#D8DBE0',
  agreementStatusDraft: BASE_COLORS.blue,
  agreementStatusOpen: BASE_COLORS.green,
  get agreementStatusClosed() {
    return this.fleetSlateGray;
  },

  agreementStatusDraftLight: '#E4E7F9',
  agreementStatusOpenLight: '#EDF9EA',
  agreementStatusClosedLight: '#EFEFF1',

  removeButton: BASE_COLORS.red,

  darkGrayIcon: '#989ca8',
  lightGrayIcon: '#C4C4C4',
  switchBg: '#E7EAEF',

  statusIconCalendarRed: '#E53636',
  statusIconCalendarOrange: '#F5920A',

  inputError: '#9f3a38',
  inputErrorBackground: '#fff6f6',

  metricsHoverBackground: '#f8f8fa',

  telematicsStatusConnectedText: '#288f2d',
  telematicsStatusConnectedBackground: '#def3da',
  telematicsStatusConnectedIcon: '#33b13a',

  telematicsStatusDisconnectedText: '#e5254d',
  telematicsStatusDisconnectedBackground: '#fbe6eb',
  telematicsStatusDisconnectedIcon: '#e30a36',

  telematicsStatusReadyToConnectText: '#2a53e6',
  telematicsStatusReadyToConnectBackground: '#e5e8fb',
  telematicsStatusReadyToConnectIcon: '#1f53e6',

  telematicsStatusUndetectedVinText: '#ce7821',
  telematicsStatusUndetectedVinBackground: '#feeeda',
  telematicsStatusUndetectedVinIcon: '#db8425',
} as const;

export default colors;
